import React, { createContext, useContext, useEffect, useState } from 'react';
import { authApi, getAuthToken, setAuthToken, removeAuthToken } from '@/lib/auth';

export interface User {
  id: string;
  username: string;
  fullName: string;
  email: string;
  profileImage?: string;
  provider?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    fullName: string,
    password: string,
    provider: string
  ) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  resetPassword: (email: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem('token') || null;
  });

  const [isLoading, setIsLoading] = useState(false);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await authApi.login({ email, password });
      const normalizedUser = {
      ...response.user,
      id: String(response.user.id), // convert number to string
     };
      setUser(normalizedUser);
      setToken(response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      // localStorage.setItem('token', response.token);
      setAuthToken(response.token);
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      await authApi.resetPassword({ email, password });
      // Optionally, show a success message
    } finally {
      setIsLoading(false);
    }
  };


  const register = async (
    email: string,
    fullName: string,
    password: string,
    provider: string
  ) => {
    setIsLoading(true);
    try {
      const response = await authApi.register({ email, fullName, password, provider });
      const normalizedUser = {
        ...response.user,
        id: String(response.user.id), // convert number to string
      };
      setUser(normalizedUser);
      setToken(response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      localStorage.setItem('token', response.token);
      setAuthToken(response.token);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    removeAuthToken();
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        register,
        logout,
        isAuthenticated: !!user,
        isLoading,
        resetPassword
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
